import images from '@assets/images/index'
import { PlayerActivityModal } from '@components/ui/PlayerActivityModal/PlayerActivityModal'
import { DropdownFilters } from '@hooks/useAssessmentDropdownFilters'
import useExport from '@hooks/useExport'
import {
  CellContext,
  Column,
  HeaderContext,
  Row,
  Table
} from '@tanstack/react-table'
import { Flex, message } from 'antd'
import { ItemType } from 'antd/es/menu/interface'
import { formatDistanceToNow } from 'date-fns'
import type { RefObject } from 'react'
import { useState } from 'react'

import { useNavigate, useParams } from '@suntex.core/nav'
import { downloadPdfFromElement, printElement } from '@suntex.core/print'
import {
  Client,
  Database_Procs_GetWDYKTestAssessmentByBatch_Item,
  Database_Tables_Player,
  Entities_Assessment_GetWDYKTestBatchesByTeams_WDYKTestBatchesByTeamInfo,
  PlayerId
} from '@suntex.fim/api-client'
import {
  getTitle,
  renderHeaderTooltip,
  schoolLabels,
  teamLabels
} from '@suntex.fim/assessment'
import { isAuthenticated, useAuth } from '@suntex.fim/auth/react'
import {
  $SpecialGradeLevel,
  EntityLevel,
  SpecialGradeLevel
} from '@suntex.fim/enums'
import { flaggedItems, getFlags } from '@suntex.fim/flags'
import { useFramework } from '@suntex.fim/framework'
import { useOptionalPlayerContext } from '@suntex.fim/player'
import { useOptionalTeamInfo } from '@suntex.fim/team'
import { Dropdown } from '@suntex.fim/ui'

import { handleEnterOrSpacePress } from '../pages/app/LearningCenter/util'

import { getLocalization } from './getLocale'

export type GivenChoicesSummary = {
  length: number
} & Record<`givenChoice${number}`, string>

function handleChildParentCheckBox<T>(column: Column<T>) {
  column.getToggleVisibilityHandler()
  column.getLeafColumns().forEach((childColumn) => {
    childColumn.toggleVisibility(!column.getIsVisible())
  })
}

function getLabel<T>(
  column: Column<T>,
  hidableSelectLabel: Record<string, string>
) {
  return (
    <div
      role="checkbox"
      aria-checked={column.getIsVisible()}
      tabIndex={0}
      aria-label={`Toggle column visibility for ${hidableSelectLabel[column.id]}`}
      className="px-1"
      onClick={(e) => {
        e.stopPropagation()
        handleChildParentCheckBox(column)
      }}
      onKeyDown={(e) => {
        handleEnterOrSpacePress(e, () => handleChildParentCheckBox(column))
        e.stopPropagation()
      }}
    >
      <label htmlFor={column.id} style={{ cursor: 'pointer' }}>
        <div className="pretty p-icon p-toggle">
          <input
            id={column.id}
            type="checkbox"
            checked={column.getIsVisible()}
            onChange={() => handleChildParentCheckBox(column)}
            aria-checked={column.getIsVisible()}
            aria-label={`Toggle column visibility for ${hidableSelectLabel[column.id]}`}
          />
          <div className="state p-on">
            <images.CheckboxCheckedSm />
          </div>
          <div className="state p-off">
            <images.CheckboxSm />
          </div>
        </div>{' '}
        <span>{hidableSelectLabel[column.id]}</span>
      </label>
    </div>
  )
}

export const renderPlayed = (text: string) => {
  const orignalText = new Date(text)
  let relativeTime = formatDistanceToNow(orignalText, { addSuffix: true })
  if (relativeTime.includes('about')) {
    relativeTime = relativeTime.replace('about', '')
  }
  return (
    <Flex gap={8} align="center">
      {relativeTime}
    </Flex>
  )
}

export const playerActivityColumns = [
  {
    title: 'Game',
    dataIndex: 'name',
    key: 'name',
    id: 'gameColumn',
    render: (text: string, record: any) => {
      const imageName = `_${record.moduleId}`
      const imagesRecord = images as Record<string, any>

      // Check if the image exists; if not, use a default image
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const imageSrc = imagesRecord[imageName] || imagesRecord.NoGameIcon

      return (
        <Flex gap={8} align="center">
          {imageSrc && (
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            <img src={imageSrc} alt="game-icon" className="game-icon" />
          )}
          {text}
        </Flex>
      )
    }
  },
  {
    title: 'Stickers',
    dataIndex: 'stickers',
    key: 'stickers',
    id: 'stickersColumn'
  },
  {
    title: 'Played',
    dataIndex: 'timestamp',
    key: 'timestamp',
    id: 'playedColumn',
    render: (text: string) => {
      return renderPlayed(text)
    }
  }
]

export const getPlayerActivityDataAndShowModal = async (
  api: ReturnType<typeof useFramework>['api'],
  playerId: PlayerId,
  setIsModalOpen: React.Dispatch<React.SetStateAction<boolean>>,
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>,
  setActivityData: React.Dispatch<React.SetStateAction<any>>,
  setCount: React.Dispatch<React.SetStateAction<number | undefined>>
) => {
  setIsModalOpen(true)
  setIsLoading(true)
  try {
    const activity = await api.get_Player_Activity({
      playerId,
      top: 25,
      skip: 0
    })
    const { activityData, count } = activity
    setActivityData(activityData)
    setCount(count)
    setIsLoading(false)
  } catch (e) {
    setIsLoading(false)
  }
}

export function GetmenuItems<T>(
  info: CellContext<Database_Tables_Player | any, unknown>,
  sendMessage: (t: Row<T>[]) => void,
  getPlayerActivityDataAndShowModal: (playerId: PlayerId) => void
) {
  const team = useOptionalTeamInfo()
  const navigate = useNavigate()
  return [
    {
      key: '1',
      label: 'Fluency Games Report',
      icon: <images.SearchZoomIn />,
      onClick: () =>
        navigate(
          `/team/${team?.id}/assessment-report/fluency-games?playerId=${info.row.original.userId}`
          // `/player/${info.row.original.playerId || info.row.original.id}/assessment-report/fluency-games`
        )
    },
    {
      key: '2',
      label: 'Player Activity',
      icon: <images.FlashCircle />,
      onClick: () => getPlayerActivityDataAndShowModal(info.row.original.id)
    },
    {
      key: '3',
      icon: <images.EmailFill />,
      label: 'Message Player',
      onClick: () => sendMessage([info.row])
    },
    ...flaggedItems(getFlags().player.goals, [
      {
        key: '4',
        label: 'Goals Report',
        icon: <images.SearchZoomIn />,
        onClick: () =>
          navigate(
            `/player/${info.row.original.playerId || info.row.original.id}/my-goals`
          )
      }
    ])
  ]
}

export function GetCell<T>(
  info: CellContext<Database_Tables_Player | any, unknown>,
  sendMessage: (t: Row<T>[]) => void
) {
  const { api } = useFramework()
  const [isModalOpen, setIsModalOpen] = useState(false)

  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const [activityData, setActivityData] = useState<any>([])
  const [count, setCount] = useState<number | undefined>(0)
  const [isLoading, setIsLoading] = useState(false)

  const closeModal = () => {
    setIsModalOpen(false)
  }

  return (
    <>
      <Dropdown
        label=""
        ariaLabel="Player actions menu"
        menu={{
          items: GetmenuItems(info, sendMessage, () =>
            getPlayerActivityDataAndShowModal(
              api,
              info.row.original?.playerId || info.row.original?.id,
              setIsModalOpen,
              setIsLoading,
              setActivityData,
              setCount
            )
          )
        }}
        className="table-cell-icon"
        overlayClassName="table-cell-icon"
        icon={<images.SquareMenu />}
      />
      <PlayerActivityModal
        isModalOpen={isModalOpen}
        handleOk={closeModal}
        handleCancel={closeModal}
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        dataSource={activityData}
        columns={playerActivityColumns}
        count={count}
        isLoading={isLoading}
      />
    </>
  )
}

const getMappedKeyLabel = (
  column: any,
  hidableSelectLabel: Record<string, string>
) => {
  return column.type === 'group'
    ? {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        key: column.id,
        type: 'group',
        label: getLabel(column, hidableSelectLabel),
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        children: column.children
      }
    : {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        key: column.id,
        label: getLabel(column, hidableSelectLabel)
      }
}

export function actionColumn<T>(
  hidableSelectLabel: Record<string, string>,
  sendMessage: (t: Row<T>[]) => void
) {
  return {
    accessorKey: 'action',
    id: 'actions',
    size: 50,
    enableSorting: false,
    header: (header: HeaderContext<T, unknown>) => {
      const items = header.table
        .getAllColumns()
        .map(({ columns, ...rest }) =>
          columns.length
            ? {
                ...rest,
                type: 'group',
                children: columns
                  .filter((column: Column<T, unknown>) =>
                    Object.keys(hidableSelectLabel).includes(column.id)
                  )
                  .map((column: Column<T, unknown>) =>
                    getMappedKeyLabel(column, hidableSelectLabel)
                  )
              }
            : { ...rest, columns }
        )
        .filter((column: { id: string }) =>
          Object.keys(hidableSelectLabel).includes(column.id)
        )
        .map((column) => getMappedKeyLabel(column, hidableSelectLabel)) as
        | ItemType[]
        | undefined
      return (
        <Dropdown
          label=""
          ariaLabel="Table settings menu"
          menu={{ items }} //@Hitesh, please review
          className="table-header-icon"
          overlayClassName="table-header-icon"
          icon={<images.SettingTable />}
        />
      )
    },
    cell: (info: CellContext<T, unknown>) => {
      return GetCell(info, sendMessage)
    }
  }
}

export function commonActionColumn<T>(
  hidableSelectLabel: Record<string, string>,
  sendMessage: (t: Row<T>[]) => void
) {
  return {
    accessorKey: 'action',
    id: 'actions',
    size: 50,
    enableSorting: false,
    header: (header: HeaderContext<T, unknown>) => {
      const items = header.table
        .getAllFlatColumns()
        .filter((column) => Object.keys(hidableSelectLabel).includes(column.id))
        .map((column) => ({
          key: column.id,
          label: getLabel(column, hidableSelectLabel)
        }))
      return (
        <Dropdown
          label=""
          ariaLabel="Table settings menu"
          menu={{ items }}
          className="table-header-icon"
          overlayClassName="table-header-icon"
          icon={<images.SettingTable />}
        />
      )
    },
    cell: (info: CellContext<T, unknown>) => {
      return GetCell(info, sendMessage)
    }
  }
}

export const hasIsPrimaryTeam = (obj: any): obj is { isPrimaryTeam: boolean } =>
  'isPrimaryTeam' in obj

export const isBetaVersion = () => {
  return window.location.href.split('/')[2].includes('beta')
}
export const getBaseURL = () => {
  let baseUrl = 'https://dev.firstinmath.com'

  if (isBetaVersion()) {
    baseUrl = 'https://www.firstinmath.com'
  }
  return getLocalization().rootUrl(baseUrl)
}

export const hasIsLevelUp = (
  obj: any
): obj is { activity1W: number; activity2W: number } => {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const { activity1W, activity2W } = obj.original
  return activity2W < activity1W
}

/**
 * Opens the browser print dialog for a target element (default: document.body).
 * Clones the element into a hidden iframe so the host page is unaffected.
 *
 * @param prepare - Optional async callback invoked before cloning. Use to expand
 *   pagination, fetch data, or otherwise prepare the DOM for printing.
 * @param cleanup - Optional callback invoked after the print dialog closes.
 *   Use to restore pagination or other temporary state changes.
 */
export async function handlePrint({
  setIsLoading,
  element,
  fileName,
  prepare,
  cleanup
}: {
  setIsLoading?: (loading: boolean) => void
  element?: HTMLElement | null
  fileName?: string
  prepare?: () => Promise<void> | void
  cleanup?: () => void
} = {}): Promise<void> {
  const target = element ?? document.body

  try {
    if (prepare) {
      await prepare()
      // Wait for React to commit the re-render (expanded rows, etc.)
      await new Promise<void>((resolve) =>
        requestAnimationFrame(() => resolve())
      )
    }

    await printElement(target, {
      title: fileName,
      onBeforePrint: () => setIsLoading?.(true)
    })
  } finally {
    cleanup?.()
    setIsLoading?.(false)
  }
}

export async function printFullTable<T>(
  table: Table<T>,
  options: {
    setIsLoading?: (loading: boolean) => void
    fileName?: string
  }
): Promise<void> {
  const currentPagination = table.getState().pagination
  return handlePrint({
    ...options,
    prepare: () => {
      table.setPageSize(table.getRowCount())
    },
    cleanup: () => {
      table.setPagination(currentPagination)
    }
  })
}

/**
 * Captures the given element as a PDF and triggers a browser download.
 */
async function handleDownloadPdf({
  element,
  fileName = 'report',
  setIsLoading
}: {
  element: HTMLElement
  fileName?: string
  setIsLoading?: (loading: boolean) => void
}): Promise<void> {
  setIsLoading?.(true)
  try {
    await downloadPdfFromElement(element, { fileName })
    message.success('PDF downloaded successfully.')
  } catch {
    message.error('Failed to generate PDF.')
  } finally {
    setIsLoading?.(false)
  }
}

export const useDropdownMenu = ({
  table,
  hidableSelectLabel = {},
  fileName = 'report',
  hasColumnChange = [],
  transformCols = () => {},
  isSchool = false,
  setIsLoading,
  hidePrintPdf = false,
  printableRef
}: {
  table: Table<any>
  hidableSelectLabel: Record<string, string>
  fileName: string
  hasColumnChange?: string[]
  transformCols?: any
  isSchool?: boolean
  setIsLoading?: (loading: boolean) => void
  hidePrintPdf?: boolean
  /** Ref to the element to capture when "Download as PDF" is clicked. */
  printableRef?: RefObject<HTMLElement | null>
}) => {
  const { exportSelectedToExcel } = useExport({
    table,
    hidableSelectLabel,
    fileName: `${fileName}`,
    hasColumnChange,
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    transformCols,
    isSchool
  })

  const items: { label: string; key: string; onClick: () => void }[] = [
    {
      label: 'Export to Excel',
      key: 'excel',
      onClick: () => {
        if (table.getRowModel().rows.length) {
          exportSelectedToExcel()
        } else {
          message.error('No Data to Export')
        }
      }
    }
  ]
  if (!hidePrintPdf) {
    items.push({
      label: 'View Printer Friendly Version',
      key: 'print',
      onClick: () => {
        void printFullTable(table, { setIsLoading, fileName })
      }
    })
    if (printableRef) {
      items.push({
        label: 'Download as PDF',
        key: 'pdf',
        onClick: () => {
          if (printableRef.current) {
            void handleDownloadPdf({
              element: printableRef.current,
              fileName,
              setIsLoading
            })
          }
        }
      })
    }
  }
  return items
}

// Function to remove searchParams from the URL
export function removeSearchParams(searchParams: string) {
  const url = new URL(window.location.href) // Get the current URL
  url.searchParams.delete(searchParams) // Remove searchParams parameter
  window.history.replaceState(null, '', url) // Update the URL without reloading
}
export const renderData = (data?: string, emptyVal?: string) =>
  data && data !== '-' ? data : emptyVal || '-'
const extractBracketContent = (input: string) => {
  const match = input.match(/\[.*?\]/) // Matches content within square brackets
  return match ? match[0] : '' // Return the matched content or null if not found
}
export type GameContent = Record<string, any>
type ModuleName = { url: string; text: string }

export const generateModifiedContent = (
  gameContent: GameContent,
  getMessageContent: (result: string) => ModuleName
): string => {
  const moduleNames: ModuleName[] = []

  Object.entries(gameContent).forEach(([key, value]) => {
    if (value) {
      let result = ''

      if (key.startsWith('Module')) {
        const subModuleName = key.replace('Module-', '')
        const colonIndex = subModuleName.indexOf(':')
        if (colonIndex !== -1) {
          result = subModuleName.substring(colonIndex + 1).trim()
        }
      } else {
        result = extractBracketContent(key)
      }

      const message = getMessageContent(result)
      moduleNames.push(message)
    }
  })

  const modifiedModuleNames = moduleNames.map(
    ({ url, text }) =>
      `<a href=${url} target="_blank" rel="noreferrer">${text}</a>`
  )

  return modifiedModuleNames.join('<br>')
}

export const GetAPIparams = (
  isSchool: boolean,
  dropdownFilters?: DropdownFilters,
  isPlayer?: boolean
): {
  id: number | undefined
  year: string | undefined
  grade: SpecialGradeLevel
  sortingkey: string
  level: EntityLevel
} => {
  const { login } = useAuth(isAuthenticated)
  const team = useOptionalTeamInfo()
  const playerCtx = useOptionalPlayerContext()
  const player = playerCtx?.playerInfo ?? null
  const playerTeam = playerCtx?.playerInfo ?? null
  const params = useParams()
  let id: number | undefined
  let year: string | undefined
  let grade: SpecialGradeLevel
  let sortingkey: string
  let level: EntityLevel

  if (isPlayer) {
    // For player routes, prefer params.playerId, then player state fallback
    id = params.playerId ? Number(params.playerId) : playerCtx?.playerId
    year = login?.year
    grade =
      $SpecialGradeLevel.fromOptional(player?.player.grade) ??
      SpecialGradeLevel.Null
    sortingkey = 'playerName'
    level = EntityLevel.Player
  } else if (isSchool) {
    id = login?.schoolId
    year = dropdownFilters?.year
    grade =
      $SpecialGradeLevel.fromOptional(dropdownFilters?.grade) ??
      SpecialGradeLevel.Null
    sortingkey = 'teamName'
    level = EntityLevel.School
  } else {
    // For team context, prefer params.teamId over URL parsing and state fallback
    if (params.teamId && !isNaN(Number(params.teamId))) {
      id = Number(params.teamId)
    } else {
      id = team?.id ?? playerTeam?.team?.teamId ?? login.teamId
    }
    year = team?.year ?? playerTeam?.team?.year ?? login.year
    grade =
      $SpecialGradeLevel.fromOptional(
        team?.grade ?? playerTeam?.team?.grade ?? dropdownFilters?.grade
      ) ?? SpecialGradeLevel.Null
    sortingkey = 'displayName'
    level = EntityLevel.Team
  }

  return { id, year, grade, sortingkey, level }
}

export const renderGradeColumn = (value: any) => {
  return value && value !== 50 ? (
    <b>{value > 0 ? value.toLocaleString() : '-'}</b>
  ) : (
    <b>{value === 50 ? 'Other' : 0}</b>
  )
}

export const renderTitle = (isSchool: boolean) => {
  return (
    <Flex align="center" className="graph-tiles__title" justify="space-between">
      <h4 className="title">
        {isSchool ? 'School' : 'Team'} Aggregate Performance
      </h4>
    </Flex>
  )
}
export const calculateSummary = (teams: any[]) => {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const total = teams.reduce(
    (acc, team) => ({
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      totalStudents: acc.totalStudents + team.students,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      totalStickers: acc.totalStickers + team.stickers,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      totalTimeOnTask: acc.totalTimeOnTask + team.timeOnTask,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      totalProblemsSolved: acc.totalProblemsSolved + team.problemsSolved,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      totalMathematicians: acc.totalMathematicians + team.mathematicians,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      totalGrandChampions: acc.totalGrandChampions + team.grandChampions,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      totalPercentMathematicians:
        acc.totalPercentMathematicians + team.percentMathematicians,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      totalPercentGrandChampions:
        acc.totalPercentGrandChampions + team.percentGrandChampions
    }),
    {
      totalStudents: 0,
      totalStickers: 0,
      totalTimeOnTask: 0,
      totalProblemsSolved: 0,
      totalMathematicians: 0,
      totalGrandChampions: 0,
      totalPercentMathematicians: 0,
      totalPercentGrandChampions: 0
    }
  )

  return {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    totalStudents: total.totalStudents,
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    totalStickers: total.totalStickers,
    totalStickersPerStudent: total.totalStickers / total.totalStudents,
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    totalTimeOnTask: total.totalTimeOnTask,
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    totalProblemsSolved: total.totalProblemsSolved,
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    totalMathematicians: total.totalMathematicians,
    averagePercentMathematicians:
      total.totalStudents > 0
        ? (total.totalMathematicians / total.totalStudents) * 100
        : 0,
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    totalGrandChampions: total.totalGrandChampions,
    averagePercentGrandChampions:
      total.totalStudents > 0
        ? (total.totalGrandChampions / total.totalStudents) * 100
        : 0
  }
}
// getTitle, schoolLabels, teamLabels, and renderHeaderTooltip are implemented in @suntex.fim/assessment
// and re-exported here for backwards compatibility.
export { getTitle, renderHeaderTooltip, schoolLabels, teamLabels }

// function moment(text: string) {
//     throw new Error('Function not implemented.')
// }
// function formatDate(dateString?: string | Date): string {
//   if (!dateString) return '' // Ensure function always returns a string (fixes eslint `consistent-return`)

//   try {
//     const date =
//       typeof dateString === 'string' ? parseISO(dateString) : dateString // Ensure it's a Date object
//     let newDate = date
//     if (date.toString().includes('India Standard Time')) {
//       newDate = addHours(date, 5)
//     }
//     return format(newDate, "MMMM d, yyyy '@' h:mm:ss a") // Format correctly
//   } catch (error) {
//     console.error('Invalid date:', dateString)
//     return 'Invalid Date'
//   }
// }

export async function getWDYKReportQuestion(
  api: Pick<Client, 'get_Player_WdykTemplateQuestions'>,
  testTemplateId: number,
  columnId: string
) {
  if (!/^_(?:[1-9]|10)$/.test(columnId) || testTemplateId <= 0) {
    throw new Error('Invalid WDYK question selection')
  }
  const questionNumber = Number(columnId.slice(1))
  const response = await api.get_Player_WdykTemplateQuestions({
    testTemplateId,
    questionNumber
  })
  const question = response.wdykQuestions.find(
    (question) => question.questionNumber === questionNumber
  )
  if (!question?.question) throw new Error('WDYK question not found')
  return question
}

export const formatTestsSummary = (
  tests:
    | Entities_Assessment_GetWDYKTestBatchesByTeams_WDYKTestBatchesByTeamInfo[]
    | undefined
) => {
  if (!tests) return {}
  return tests.reduce(
    (acc, test) => {
      if (test.id === undefined) return acc // Skip if ID is missing
      // const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
      // const utcDate = new Date(`${test.created}Z`)
      // const zonedDate = toZonedTime(utcDate, timezone)
      // const formattedDate = formatDate(zonedDate)
      acc[test.id] = `${test.topic} -- Grade ${test.grade} -- ${test.created}`
      return acc
    },
    {} as Record<number, string>
  ) // Explicitly typing the accumulator
}
export const calculateGivenChoicesSummary = (
  players: Database_Procs_GetWDYKTestAssessmentByBatch_Item[]
): GivenChoicesSummary => {
  //a type for the numbers 1...10
  type OneToTen = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10

  //map each number to a key with a leading underscore
  type UnderscoredOneToTen = {
    [K in OneToTen as `_${K}`]: number
  }

  //the totals are the fields _1 ... _10
  const totals = {} as UnderscoredOneToTen

  players.forEach((player) => {
    ;(
      Object.keys(player) as Array<
        keyof Database_Procs_GetWDYKTestAssessmentByBatch_Item
      >
    )
      .filter((key) => key.startsWith('_'))
      .forEach((key) => {
        const playerData = player as UnderscoredOneToTen

        totals[key] =
          `${(totals[key] ? parseInt(totals[key], 10) : 0) + (playerData[key] > 0 ? 1 : 0)}`
      })
  })
  return {
    ...totals,
    length: players.length
  }
}

export const getIconComponent = (value: number) => {
  if (value > 0) return <images.WdykCheckBoxChecked />
  if (value === 0) return <images.CheckBoxCross />
  return <images.CheckboxEmptyLg />
}

export const getFixedValue = (value: number) => {
  return Number(value.toFixed(1)).toLocaleString()
}
